import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ROUTES } from "@/config/routes.config";
import { PublicRoute } from "./PublicRoute";
import { ProtectedRoute } from "./ProtectedRoute";
import { HRRoute } from "./HRRoute";
import { ManagerOrHRRoute } from "./ManagerOrHRRoute";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { AuthLayout } from "@/layouts/AuthLayout";

// Lazily load page views
const LoginPage = lazy(() => import("@/features/auth/LoginPage"));
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage"));
const EmployeeListPage = lazy(() =>
  import("@/features/employees/EmployeeListPage"),
);
const EmployeeProfilePage = lazy(() =>
  import("@/features/employees/EmployeeProfilePage"),
);
const MyTeamPage = lazy(() => import("@/features/team/MyTeamPage"));
const ConfirmationReviewPage = lazy(() => import("@/features/team/ConfirmationReviewPage"));
const MyInfoPage = lazy(() => import("@/features/employees/MyInfoPage"));
const CandidatesPage = lazy(() =>
  import("@/features/candidates/CandidatesPage"),
);
const ExitsPage = lazy(() => import("@/features/exits/ExitsPage"));
const OrgChartPage = lazy(() => import("@/features/org/OrgChartPage"));
const LeaveDashboardPage = lazy(() =>
  import("@/features/leave/LeaveDashboardPage"),
);
const LeaveReportingPage = lazy(() =>
  import("@/features/leave/LeaveReportingPage"),
);
const DocumentReportingPage = lazy(() => import("@/features/documents/DocumentReportingPage"));
const PolicyDocumentsPage = lazy(() =>
  import("@/features/documents/PolicyDocumentsPage"),
);
const SettingsHubPage = lazy(() =>
  import("@/features/settings/SettingsHubPage"),
);
const ProfessionalSettingsPage = lazy(() =>
  import("@/features/settings/ProfessionalSettingsPage"),
);
const AppraisalCyclesPage = lazy(() =>
  import("@/features/settings/AppraisalCyclesPage"),
);
const AppraisalReportingPage = lazy(() =>
  import("@/features/performance-appraisal/AppraisalReportingPage"),
);
const LmsSettingsPage = lazy(() =>
  import("@/features/settings/LmsSettingsPage"),
);
const LeaveCyclesPage = lazy(() =>
  import("@/features/settings/LeaveCyclesPage"),
);
const PolicyAdminPage = lazy(() =>
  import("@/features/documents/PolicyAdminPage"),
);
const RolesPermissionsPage = lazy(() =>
  import("@/features/roles/RolesPermissionsPage"),
);
const PerformanceAppraisalPage = lazy(() =>
  import("@/features/performance-appraisal/PerformanceAppraisalPage"),
);
const SubordinateAppraisalPage = lazy(() =>
  import("@/features/performance-appraisal/SubordinateAppraisalPage"),
);

const EmployeeReportingPage = lazy(() => import("@/features/employees/EmployeeReportingPage"));
const SalaryAdvancePage = lazy(() =>
  import("@/features/salary-advance/SalaryAdvancePage"),
);
const SalaryAdvanceApprovalsPage = lazy(() =>
  import("@/features/salary-advance/SalaryAdvanceApprovalsPage"),
);
const SalaryAdvanceReportingPage = lazy(() =>
  import("@/features/salary-advance/SalaryAdvanceReportingPage"),
);

export const AppRoutes: React.FC = () => {
  return (
    <Suspense
      fallback={
        <div className="flex h-screen w-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent"></div>
        </div>
      }
    >
      <Routes>
        {/* Auth routes */}
        <Route element={<PublicRoute />}>
          <Route element={<AuthLayout />}>
            <Route path={ROUTES.LOGIN} element={<LoginPage />} />
            <Route path={ROUTES.REGISTER} element={<Navigate to={ROUTES.LOGIN} replace />} />
            <Route
              path={ROUTES.FORGOT_PASSWORD}
              element={<Navigate to={ROUTES.LOGIN} replace />}
            />
          </Route>
        </Route>

        {/* Dashboard / Protected App routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<DashboardLayout />}>
            <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />

            <Route element={<HRRoute />}>
              <Route path={ROUTES.DOCUMENT_REPORTING} element={<DocumentReportingPage />} />
              {/* Employee routes */}
              <Route path={ROUTES.EMPLOYEES} element={<EmployeeListPage />} />
              <Route path={ROUTES.EMPLOYEE_REPORTING} element={<EmployeeReportingPage />} />
              <Route
                path={ROUTES.EMPLOYEE_PROFILE}
                element={<EmployeeProfilePage />}
              />
              <Route path={ROUTES.CANDIDATES} element={<CandidatesPage />} />
              <Route path={ROUTES.EXITS} element={<ExitsPage />} />
              <Route path={ROUTES.LEAVE_REPORTING} element={<LeaveReportingPage />} />
              <Route path={ROUTES.APPRAISAL_REPORTING} element={<AppraisalReportingPage />} />
              <Route path={ROUTES.SALARY_ADVANCE_REPORTING} element={<SalaryAdvanceReportingPage />} />
            </Route>

            <Route path={ROUTES.MY_INFO} element={<MyInfoPage />} />
            <Route path={ROUTES.MY_TEAM} element={<MyTeamPage />} />
            <Route path={ROUTES.ORG_CHART} element={<OrgChartPage />} />
            <Route path={ROUTES.TEAM_CONFIRMATION} element={<ConfirmationReviewPage />} />

            {/* Leave management */}
            <Route path={ROUTES.LEAVE} element={<LeaveDashboardPage />} />

            {/* Policies */}
            <Route path={ROUTES.DOCUMENTS} element={<PolicyDocumentsPage />} />

            {/* Salary Advance */}
            <Route path={ROUTES.SALARY_ADVANCE} element={<SalaryAdvancePage />} />
            <Route element={<ManagerOrHRRoute />}>
              <Route path={ROUTES.SALARY_ADVANCE_APPROVALS} element={<SalaryAdvanceApprovalsPage />} />
            </Route>

            {/* Performance Appraisals */}
            <Route path={ROUTES.PERFORMANCE_APPRAISALS} element={<PerformanceAppraisalPage />} />
            <Route path={ROUTES.PERFORMANCE_APPRAISAL_SUBORDINATE} element={<SubordinateAppraisalPage />} />

            {/* Settings */}
              <Route path={ROUTES.SETTINGS} element={<SettingsHubPage />} />
              <Route
                path={ROUTES.SETTINGS_PROFESSIONAL}
                element={<ProfessionalSettingsPage />}
              />
              <Route
                path={ROUTES.APPRAISAL_CYCLES}
                element={<AppraisalCyclesPage />}
              />
              <Route path={ROUTES.SETTINGS_LMS} element={<LmsSettingsPage />} />
              <Route path={ROUTES.SETTINGS_LEAVE_CYCLES} element={<LeaveCyclesPage />} />
              <Route
                path={ROUTES.SETTINGS_POLICIES_ADMIN}
                element={<PolicyAdminPage />}
              />
              <Route path={ROUTES.ROLES} element={<RolesPermissionsPage />} />
            </Route>
          </Route>

        <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
      </Routes>
    </Suspense>
  );
};
